/**
 * Call History Panel Component
 * Displays call logs, recordings, and call analytics
 */

import React, { useState, useEffect } from 'react';
import './CallHistoryPanel.css';

const CallHistoryPanel = ({ userPhoneNumbers = [] }) => {
  const [callLogs, setCallLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('all');
  const [callDirection, setCallDirection] = useState('all');
  const [dateRange, setDateRange] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [stats, setStats] = useState({});
  const [playingCallId, setPlayingCallId] = useState(null);
  const [audioRef] = useState({ current: null });

  useEffect(() => {
    loadCallHistory();
  }, []);

  useEffect(() => {
    filterCalls();
  }, [callLogs, selectedPhoneNumber, callDirection, dateRange]);

  const loadCallHistory = async () => {
    try {
      setLoading(true);
      
      // Load call history and stats in parallel
      const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('nf_token')}` };
      const [callsResponse, statsResponse] = await Promise.all([
        fetch('/api/callhistory', { headers: authHeaders }),
        fetch('/api/callhistory/stats', { headers: authHeaders })
      ]);
      
      if (!callsResponse.ok) {
        throw new Error(`HTTP ${callsResponse.status}: ${callsResponse.statusText}`);
      }
      
      const calls = await callsResponse.json();
      const stats = statsResponse.ok ? await statsResponse.json() : {};
      
      setCallLogs(calls);
      setStats(stats);
    } catch (err) {
      console.error('Error loading call history:', err);
      setCallLogs([]);
      setStats({});
      setError('Unable to load call history');
    } finally {
      setLoading(false);
    }
  };

  const filterCalls = () => {
    let filtered = [...callLogs];

    // Filter by phone number
    if (selectedPhoneNumber !== 'all') {
      filtered = filtered.filter(call => (call.userNumber || call.phoneNumber || '') === selectedPhoneNumber);
    }

    // Filter by direction (including voicemail filter)
    if (callDirection === 'voicemail') {
      filtered = filtered.filter(call => call.recordingUrl);
    } else if (callDirection !== 'all') {
      filtered = filtered.filter(call => call.direction === callDirection);
    }

    // Filter by date range
    const now = new Date();
    let startDate = new Date();
    
    switch (dateRange) {
      case '1day':
        startDate.setDate(now.getDate() - 1);
        break;
      case '7days':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90days':
        startDate.setDate(now.getDate() - 90);
        break;
      default:
        startDate = new Date(0); // All time
    }

    filtered = filtered.filter(call => new Date(call.startTime) >= startDate);
    
    // Sort by most recent first
    filtered.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

    setFilteredLogs(filtered);
  };

  const formatDuration = (seconds) => {
    if (seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    if (cost == null) return 'Free';
    return cost === 0 ? 'Free' : `$${cost.toFixed(2)}`;
  };

  const formatDateTime = (date) => {
    const callDate = new Date(date);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const callDateOnly = new Date(callDate.getFullYear(), callDate.getMonth(), callDate.getDate());

    let dateStr;
    if (callDateOnly.getTime() === today.getTime()) {
      dateStr = 'Today';
    } else if (callDateOnly.getTime() === yesterday.getTime()) {
      dateStr = 'Yesterday';
    } else {
      dateStr = callDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }

    const timeStr = callDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${timeStr}`;
  };

  const getCallStatusIcon = (status, direction) => {
    switch (status) {
      case 'completed':
        return direction === 'inbound' ? '📞' : '📱';
      case 'missed':
        return '❌';
      case 'no-answer':
        return '📵';
      case 'busy':
        return '📴';
      default:
        return '📞';
    }
  };

  const openCallDetails = (call) => {
    setSelectedCall(call);
    setShowCallModal(true);
  };

  const playRecording = (callId) => {
    // If already playing this call, stop it
    if (playingCallId === callId && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingCallId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const token = localStorage.getItem('nf_token');
    const audio = new Audio(`/api/callhistory/recording/${callId}?token=${encodeURIComponent(token)}`);
    audioRef.current = audio;
    setPlayingCallId(callId);

    audio.onended = () => {
      setPlayingCallId(null);
      audioRef.current = null;
    };
    audio.onerror = () => {
      setPlayingCallId(null);
      audioRef.current = null;
      setError('Failed to play recording');
      setTimeout(() => setError(null), 3000);
    };
    audio.play().catch(() => {
      setPlayingCallId(null);
      audioRef.current = null;
    });
  };

  const exportCallHistory = () => {
    // Convert filtered logs to CSV and download
    const headers = ['Date', 'Time', 'My Number', 'Contact', 'Direction', 'Duration', 'Status', 'Cost'];
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(call => [
        new Date(call.startTime).toLocaleDateString(),
        new Date(call.startTime).toLocaleTimeString(),
        call.userNumber,
        call.otherParty,
        call.direction,
        formatDuration(call.duration),
        call.status,
        call.cost
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="call-history-panel">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading call history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="call-history-panel">
      <div className="panel-header">
        <h2>Call History</h2>
        <button 
          className="btn btn-secondary"
          onClick={exportCallHistory}
        >
           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg></div>
          <div className="stat-content">
            <h3>{stats.totalCalls || 0}</h3>
            <p>Total Calls</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
          <div className="stat-content">
            <h3>{formatDuration(stats.totalDuration || 0)}</h3>
            <p>Total Duration</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div>
          <div className="stat-content">
            <h3>${(stats.totalCost || 0).toFixed(2)}</h3>
            <p>Total Cost</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg></div>
          <div className="stat-content">
            <h3>{stats.missedCalls || 0}</h3>
            <p>Missed Calls</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Phone Number:</label>
          <select 
            value={selectedPhoneNumber}
            onChange={(e) => setSelectedPhoneNumber(e.target.value)}
          >
            <option value="all">All Numbers</option>
            {userPhoneNumbers.map(phone => (
              <option key={phone.number || phone} value={phone.number || phone}>
                {phone.number || phone} {phone.label && `(${phone.label})`}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Direction:</label>
          <select 
            value={callDirection}
            onChange={(e) => setCallDirection(e.target.value)}
          >
            <option value="all">All Calls</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="voicemail">Voicemail</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Period:</label>
          <select 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="1day">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Call Logs */}
      <div className="call-logs">
        {filteredLogs.length === 0 ? (
          <div className="empty-state">
             <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#8892b0" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
            <h3>No Calls Found</h3>
            <p>No calls match your current filters</p>
          </div>
        ) : (
          <div className="logs-list">
            {filteredLogs.map(call => (
              <div 
                key={call.id} 
                className={`call-item ${call.status}`}
                onClick={() => openCallDetails(call)}
              >
                <div className="call-icon">
                  {call.recordingUrl ? '\u{1F4E8}' : getCallStatusIcon(call.status, call.direction)}
                </div>

                <div className="call-info">
                  <div className="call-contact">
                    <span className="contact-name">
                      {call.otherParty}
                      {call.recordingUrl && <span className="voicemail-badge">VM</span>}
                      {call.routing === 'direct_bypass' && <span className="routing-badge" title="Directly forwarded to you — bypassed AI">⚡ Fwd</span>}
                      {call.routing === 'ai_assistant' && <span className="routing-badge ai-badge" title="Handled by AI Assistant">🤖 AI</span>}
                    </span>
                    <span className="call-direction">
                      {call.direction === 'inbound' ? '\u2192' : '\u2190'} {call.userNumber}
                    </span>
                  </div>
                  <div className="call-meta">
                    <span className="call-time">{formatDateTime(call.startTime)}</span>
                    <span className="call-duration">{formatDuration(call.duration)}</span>
                    <span className="call-cost">{formatCost(call.cost)} {call.priceUnit || ''}</span>
                  </div>
                </div>

                <div className="call-actions">
                  {call.recordingUrl && (
                    <button
                      className={`action-btn recording-btn ${playingCallId === call.id ? 'playing' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        playRecording(call.id);
                      }}
                      title={playingCallId === call.id ? "Stop" : "Play Voicemail"}
                    >
                      {playingCallId === call.id ? '\u23F9' : '\u25B6'}
                    </button>
                  )}
                  <button 
                    className="action-btn callback-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Implement callback functionality
                      alert(`Calling back ${call.contactNumber}`);
                    }}
                    title="Call Back"
                  >
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call Details Modal */}
      {showCallModal && selectedCall && (
        <div className="callhistory-modal modal-overlay" onClick={() => setShowCallModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Call Details</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCallModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <CallDetailsModal call={selectedCall} onClose={() => setShowCallModal(false)} />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-toast">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}
    </div>
  );
};

// Call Details Modal Component
const CallDetailsModal = ({ call, onClose }) => {
  const [modalPlaying, setModalPlaying] = React.useState(false);
  const [modalAudio, setModalAudio] = React.useState(null);

  const playModalRecording = () => {
    if (modalPlaying && modalAudio) {
      modalAudio.pause();
      setModalPlaying(false);
      setModalAudio(null);
      return;
    }

    const token = localStorage.getItem('nf_token');
    const audio = new Audio(`/api/callhistory/recording/${call.id}?token=${encodeURIComponent(token)}`);
    setModalAudio(audio);
    setModalPlaying(true);

    audio.onended = () => { setModalPlaying(false); setModalAudio(null); };
    audio.onerror = () => { setModalPlaying(false); setModalAudio(null); };
    audio.play().catch(() => { setModalPlaying(false); setModalAudio(null); });
  };

  const downloadRecording = () => {
    const token = localStorage.getItem('nf_token');
    const link = document.createElement('a');
    link.href = `/api/callhistory/recording/${call.id}?token=${encodeURIComponent(token)}`;
    link.download = `voicemail-${call.id}.mp3`;
    link.click();
  };

  return (
    <div className="call-details">
      <div className="detail-header">
        <div className="call-status-badge">
          <span className={`status ${call.status}`}>
            {getCallStatusIcon(call.status, call.direction)} {call.status}
          </span>
        </div>
        <div className="call-direction-badge">
          {call.recordingUrl ? 'Voicemail' : (call.direction === 'inbound' ? 'Incoming Call' : 'Outgoing Call')}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-item">
          <label>Other Party:</label>
          <span>{call.otherParty || call.contactNumber || 'Unknown'}</span>
        </div>
        <div className="detail-item">
          <label>My Number:</label>
          <span>{call.userNumber || call.phoneNumber}</span>
        </div>
        <div className="detail-item">
          <label>Start Time:</label>
          <span>{new Date(call.startTime).toLocaleString()}</span>
        </div>
        {call.endTime && (
          <div className="detail-item">
            <label>End Time:</label>
            <span>{new Date(call.endTime).toLocaleString()}</span>
          </div>
        )}
        <div className="detail-item">
          <label>Duration:</label>
          <span>{formatDuration(call.duration)}</span>
        </div>
        <div className="detail-item">
          <label>Cost:</label>
          <span>{formatCost(call.cost)}</span>
        </div>
      </div>

      {call.recordingUrl && (
        <div className="recording-section">
          <h4>Voicemail Recording</h4>
          <div className="recording-player">
            <button className="btn btn-primary" onClick={playModalRecording}>
              {modalPlaying ? '\u23F9 Stop' : '\u25B6 Play'}
            </button>
            <button className="btn btn-secondary" onClick={downloadRecording}>
              Download
            </button>
          </div>
        </div>
      )}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

// Helper function for modal (needs to be accessible)
const getCallStatusIcon = (status, direction) => {
  switch (status) {
    case 'completed':
      return direction === 'inbound' ? '📞' : '📱';
    case 'missed':
      return '❌';
    case 'no-answer':
      return '📵';
    case 'busy':
      return '📴';
    default:
      return '📞';
  }
};

// Helper function for modal
const formatDuration = (seconds) => {
  if (seconds === 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Helper function for modal
const formatCost = (cost) => {
  if (cost == null) return 'Free';
  return cost === 0 ? 'Free' : `$${cost.toFixed(2)}`;
};

export default CallHistoryPanel;